import { useState, useEffect } from 'react'
import useAuth from '../../hooks/useAuth'
import PageLayout from '../common/layout/PageLayout'
import ApiAwards from '../../api/ApiAwards'
import ApiPlayers from '../../api/ApiPlayers'
import toastr from 'toastr'

function AdminAwardGrant() {
  const { isAdmin, loading: authLoading } = useAuth()
  const [loading, setLoading] = useState(false)
  const [players, setPlayers] = useState([])
  const [awardDefinitions, setAwardDefinitions] = useState([])

  const [formData, setFormData] = useState({
    user_id: '',
    award_code: '',
    context: {
      season: '',
      division_id: '',
      league_id: '',
    },
  })

  useEffect(() => {
    if (isAdmin) {
      loadPlayers()
      loadAwardDefinitions()
    }
  }, [isAdmin])

  const loadPlayers = async () => {
    try {
      const result = await ApiPlayers.getAll()
      if (result && Array.isArray(result)) {
        const sortedPlayers = result.sort((a, b) =>
          `${a.first_name} ${a.last_name}`.localeCompare(
            `${b.first_name} ${b.last_name}`,
          ),
        )
        setPlayers(sortedPlayers)
      }
    } catch {
      // Error loading players
      toastr.error('Failed to load players')
    }
  }

  const loadAwardDefinitions = async () => {
    try {
      const result = await ApiAwards.getDefinitions()
      if (result.success && result.data?.definitions) {
        setAwardDefinitions(result.data.definitions)
      }
    } catch {
      // Error loading award definitions
      toastr.error('Failed to load award definitions')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.user_id || !formData.award_code) {
      toastr.warning('Please select both a player and an award')
      return
    }

    setLoading(true)
    try {
      const payload = {
        user_id: parseInt(formData.user_id),
        award_code: formData.award_code,
      }

      if (formData.context.season) payload.season = formData.context.season

      // Validate division_id is a valid number before adding to payload
      if (formData.context.division_id) {
        const divisionId = parseInt(formData.context.division_id)
        if (!isNaN(divisionId)) {
          payload.division_id = divisionId
        }
      }

      // Validate league_id is a valid number before adding to payload
      if (formData.context.league_id) {
        const leagueId = parseInt(formData.context.league_id)
        if (!isNaN(leagueId)) {
          payload.league_id = leagueId
        }
      }

      const result = await ApiAwards.grantAward(payload)

      if (result.success) {
        const successMessage =
          result.message ||
          (result.created === false
            ? 'Award was already granted for this player.'
            : 'Award granted successfully!')
        toastr.success(successMessage)
        setFormData({
          user_id: '',
          award_code: '',
          context: { season: '', division_id: '', league_id: '' },
        })
      } else {
        toastr.error(result.error || 'Failed to grant award')
      }
    } catch (err) {
      toastr.error(err.message || 'Error granting award')
    } finally {
      setLoading(false)
    }
  }

  if (authLoading) return <p>Loading...</p>
  if (!isAdmin) return <p>Access denied. You are not an admin.</p>

  return (
    <PageLayout title="Grant Award">
      <div className="container py-4">
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-6">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">Manual Award Grant</h5>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label htmlFor="player" className="form-label">
                      Player <span className="text-danger">*</span>
                    </label>
                    <select
                      id="player"
                      data-testid="player-select"
                      className="form-select"
                      value={formData.user_id}
                      onChange={(e) =>
                        setFormData({ ...formData, user_id: e.target.value })
                      }
                      required
                    >
                      <option value="">-- Select Player --</option>
                      {players.map((p) => (
                        <option key={p.id} value={p.user_id}>
                          {p.first_name} {p.last_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="award" className="form-label">
                      Award <span className="text-danger">*</span>
                    </label>
                    <select
                      id="award"
                      data-testid="award-select"
                      className="form-select"
                      value={formData.award_code}
                      onChange={(e) =>
                        setFormData({ ...formData, award_code: e.target.value })
                      }
                      required
                    >
                      <option value="">-- Select Award --</option>
                      {awardDefinitions.map((def) => (
                        <option key={def.code} value={def.code}>
                          {def.display_name} ({def.code})
                        </option>
                      ))}
                    </select>
                    {formData.award_code && (
                      <div className="form-text">
                        {
                          awardDefinitions.find(
                            (d) => d.code === formData.award_code,
                          )?.description
                        }
                      </div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Context (Optional)</label>

                    <input
                      type="text"
                      data-testid="season-input"
                      className="form-control mb-2"
                      placeholder="Season (e.g., Fall 2024)"
                      value={formData.context.season}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          context: {
                            ...formData.context,
                            season: e.target.value,
                          },
                        })
                      }
                    />

                    <input
                      type="number"
                      data-testid="division-id-input"
                      className="form-control mb-2"
                      placeholder="Division ID"
                      min="1"
                      max="2147483647"
                      value={formData.context.division_id}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          context: {
                            ...formData.context,
                            division_id: e.target.value,
                          },
                        })
                      }
                    />

                    <input
                      type="number"
                      data-testid="league-id-input"
                      className="form-control"
                      placeholder="League ID"
                      min="1"
                      max="2147483647"
                      value={formData.context.league_id}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          context: {
                            ...formData.context,
                            league_id: e.target.value,
                          },
                        })
                      }
                    />
                    <div className="form-text">
                      Context helps track where/why the award was granted
                    </div>
                  </div>

                  <button
                    type="submit"
                    data-testid="grant-award-button"
                    className="btn btn-primary w-100"
                    disabled={loading}
                  >
                    {loading ? 'Granting Award...' : 'Grant Award'}
                  </button>
                </form>
              </div>
            </div>

            <div className="alert alert-info mt-3">
              <strong>Note:</strong> Manual grants bypass automatic awarding
              logic. The system will prevent duplicate grants for the same
              user/award/context combination.
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  )
}

export default AdminAwardGrant
